/**
 * Film Subscription Tiers
 * 
 * Defines subscription tiers for Film Tracking product.
 * Using product-specific naming to allow for future expansion
 * (e.g., Talent Basic, Marketing Pro, etc.)
 */

// Tier constants
export const FILM_TIERS = {
  FREE: 'film_free',
  BASIC: 'film_basic',
  PROFESSIONAL: 'film_professional',
  ENTERPRISE: 'film_enterprise'
};

// Tier display names
export const FILM_TIER_NAMES = {
  [FILM_TIERS.FREE]: 'Film Free',
  [FILM_TIERS.BASIC]: 'Film Basic',
  [FILM_TIERS.PROFESSIONAL]: 'Film Professional',
  [FILM_TIERS.ENTERPRISE]: 'Film Enterprise'
};

// Tier hierarchy (for comparison)
const TIER_HIERARCHY = {
  [FILM_TIERS.FREE]: 0,
  [FILM_TIERS.BASIC]: 1,
  [FILM_TIERS.PROFESSIONAL]: 2,
  [FILM_TIERS.ENTERPRISE]: 3
};

// Feature access by tier
export const FILM_TIER_PERMISSIONS = {
  [FILM_TIERS.FREE]: {
    homeWidget: true,
    dashboard: false,
    filmDetail: false,
    deepDive: false
  },
  [FILM_TIERS.BASIC]: {
    homeWidget: true,
    dashboard: true,
    filmDetail: false,
    deepDive: false
  },
  [FILM_TIERS.PROFESSIONAL]: {
    homeWidget: true,
    dashboard: true,
    filmDetail: true,
    deepDive: false
  },
  [FILM_TIERS.ENTERPRISE]: {
    homeWidget: true,
    dashboard: true,
    filmDetail: true,
    deepDive: true
  }
};

// Subscription status constants
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIAL: 'trial',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled'
};

/**
 * Check if a user has access to a specific feature
 * @param {string} userTier - User's subscription tier
 * @param {string} feature - Feature to check access for
 * @returns {boolean}
 */
export const hasFeatureAccess = (userTier, feature) => {
  const tier = userTier || FILM_TIERS.FREE;
  const permissions = FILM_TIER_PERMISSIONS[tier];
  return permissions ? permissions[feature] === true : false;
};

/**
 * Check if user tier is equal to or higher than required tier
 * @param {string} userTier - User's subscription tier
 * @param {string} requiredTier - Required tier for access
 * @returns {boolean}
 */
export const hasMinimumTier = (userTier, requiredTier) => {
  const userLevel = TIER_HIERARCHY[userTier] || 0;
  const requiredLevel = TIER_HIERARCHY[requiredTier] || 0;
  return userLevel >= requiredLevel;
};

/**
 * Get the tier required for a specific feature
 * @param {string} feature - Feature name
 * @returns {string} - Minimum tier required
 */
export const getRequiredTier = (feature) => {
  for (const [tier, permissions] of Object.entries(FILM_TIER_PERMISSIONS)) {
    if (permissions[feature] === true) {
      return tier;
    }
  }
  return FILM_TIERS.ENTERPRISE; // Default to highest tier
};

/**
 * Check if a subscription is active
 * @param {string} status - Subscription status
 * @param {Date|Timestamp} expiresAt - Expiration date
 * @returns {boolean}
 */
export const isSubscriptionActive = (status, expiresAt) => {
  if (status === SUBSCRIPTION_STATUS.CANCELLED || status === SUBSCRIPTION_STATUS.EXPIRED) {
    return false;
  }
  
  if (expiresAt) {
    const expirationDate = expiresAt.toDate ? expiresAt.toDate() : new Date(expiresAt);
    const now = new Date();
    return expirationDate > now;
  }
  
  return status === SUBSCRIPTION_STATUS.ACTIVE || status === SUBSCRIPTION_STATUS.TRIAL;
};

/**
 * Get user's effective tier considering subscription status
 * @param {string} tier - User's subscription tier
 * @param {string} status - Subscription status
 * @param {Date|Timestamp} expiresAt - Expiration date
 * @returns {string} - Effective tier (falls back to FREE if inactive)
 */
export const getEffectiveTier = (tier, status, expiresAt) => {
  if (!tier) {
    return FILM_TIERS.FREE;
  }
  
  const isActive = isSubscriptionActive(status, expiresAt);
  return isActive ? tier : FILM_TIERS.FREE;
};

/**
 * Feature descriptions for marketing/upgrade prompts
 */
export const FEATURE_DESCRIPTIONS = {
  homeWidget: 'View this week\'s film releases',
  dashboard: 'Access Film Tracking Dashboard with all survey data',
  filmDetail: 'View detailed film analytics, comps, and projections',
  deepDive: 'Access advanced demographic and behavioral analytics'
};

/**
 * Tier feature lists for pricing tables
 */
export const TIER_FEATURES = {
  [FILM_TIERS.FREE]: [
    'This week\'s releases widget',
    'Preview of Film Dashboard',
    'Preview of Film Analytics',
    'Community support'
  ],
  [FILM_TIERS.BASIC]: [
    'Everything in Film Free',
    'Full Film Dashboard access',
    'Film search and filters',
    'Survey insights overview',
    'Email support'
  ],
  [FILM_TIERS.PROFESSIONAL]: [
    'Everything in Film Basic',
    'Detailed film analytics',
    'Film comps database',
    'Box office projections',
    'IMDB integration',
    'Priority support'
  ],
  [FILM_TIERS.ENTERPRISE]: [
    'Everything in Film Professional',
    'Deep Dive analytics',
    'Demographic breakdowns',
    'Behavioral insights',
    'Multi-user company accounts',
    'Dedicated account manager'
  ]
};

/**
 * Pricing information (placeholder - should be moved to backend/env)
 */
export const TIER_PRICING = {
  [FILM_TIERS.FREE]: {
    monthly: 0,
    annual: 0
  },
  [FILM_TIERS.BASIC]: {
    monthly: 99,
    annual: 990  // 2 months free
  },
  [FILM_TIERS.PROFESSIONAL]: {
    monthly: 299,
    annual: 2990  // 2 months free
  },
  [FILM_TIERS.ENTERPRISE]: {
    monthly: null,  // Custom pricing
    annual: null
  }
};

const filmSubscriptionConfig = {
  FILM_TIERS,
  FILM_TIER_NAMES,
  FILM_TIER_PERMISSIONS,
  SUBSCRIPTION_STATUS,
  hasFeatureAccess,
  hasMinimumTier,
  getRequiredTier,
  isSubscriptionActive,
  getEffectiveTier,
  FEATURE_DESCRIPTIONS,
  TIER_FEATURES,
  TIER_PRICING
};

export default filmSubscriptionConfig;

